/**
 * Projection for `DivisionScene` — the demo's `renderDivision(v, sub)`
 * (demo line 2152) and `divHero(s, f, fs)` (demo line 2157), expressed as data.
 *
 * The demo page is four bands:
 *
 * 1. a header with an `All / <subclass> / <subclass>` tab row,
 * 2. up to four KPI cards — "All shown" plus one per subclass — each carrying
 *    mean availability, mean cost per operating hour and mean CO₂-e per hour,
 * 3. one representative machine per subclass, drawn, with a previous / select /
 *    next stepper over the machines of that class,
 * 4. the machines themselves, grouped by mine, as fleet-health cards.
 *
 * Pure: no React, no fetching, no engine mutation. Every figure comes from
 * `shared/oem-engine.ts` and `data/core.ts`; this file groups, orders and
 * labels.
 *
 * Transformation layer (layer 3 of the 4-layer architecture).
 *
 * @see src/versions/v1/modules/fleet/DivisionScene.tsx
 * @see docs/logic/fleet/division.md
 */

import type { SourceCalc } from '../../chrome-oem/SourceDrawerBody';
import { COST_PER_MACHINE, MINES, MINE_ORDER } from '../../data/core';
import type { DivisionKey, MineCode, Machine, Subclass } from '../../schema/machine';
import { ATTENTION_BANDS } from '../../schema/machine';
import {
  AVAIL_FROM,
  AVAIL_TO,
  availOf,
  DIVLABEL,
  groupNum,
  machineCardData,
  statusOf,
  SUBS,
  type DailyMetricCtx,
  type MachineCardData,
  type ShiftCtx,
} from '../../shared/oem-engine';
import type { MachineEnergy } from '../../schema/telemetry';
import type { RowFilter } from '../../shared/row-filter';
import { CALC_NOTE } from '../../shared/source-calc';
import { divisionRoute } from './AssetsSectionScene.transform';

/** The `telemetry` payload slices the division page needs. */
export interface DivisionCtx extends DailyMetricCtx, ShiftCtx {
  /** `telemetry.energy` — 100 rows, one per machine. */
  readonly energy: readonly MachineEnergy[];
}

/** `surface | drilling | loadhaul`, or `null` for anything else in the URL. */
export function parseDivision(value: string | undefined): DivisionKey | null {
  return value === 'surface' || value === 'drilling' || value === 'loadhaul' ? value : null;
}

/** `?subclass=` narrowed to the two subclasses the division actually has. */
export function parseSubclass(
  division: DivisionKey,
  value: string | null | undefined,
): Subclass | null {
  if (!value) return null;
  const match = SUBS[division].find((s) => s === value);
  return match ?? null;
}

// ---------------------------------------------------------------------------
// KPI cards
// ---------------------------------------------------------------------------

/** Demo line 2153. `stat(list)` — the three means over a set of machines. */
export interface DivisionStat {
  /** Mean availability for the reporting week, one decimal, or `null`. */
  readonly avail: number | null;
  /** Mean cost per operating hour, whole dollars, or `null`. */
  readonly cph: number | null;
  /** Mean kg CO₂-e per hour, one decimal, or `null`. */
  readonly co2: number | null;
  /** The machine ids the means were taken over — the drawer filter. */
  readonly ids: readonly string[];
}

/** Demo line 2153. Means over the machines shown, `null` where no row exists. */
export function divisionStat(machines: readonly Machine[], ctx: DivisionCtx): DivisionStat {
  const ids = machines.map((f) => f.machine_id);
  const avail = ids.map((id) => availOf(id, ctx)).filter((v): v is number => v != null);
  const cost = COST_PER_MACHINE.filter((c) => ids.includes(c.machine_id));
  const energy = ctx.energy.filter((e) => ids.includes(e.machine_id));
  return {
    avail: avail.length ? +(avail.reduce((a, b) => a + b, 0) / avail.length).toFixed(1) : null,
    cph: cost.length
      ? +(cost.reduce((a, c) => a + c.cost_per_op_hour_aud, 0) / cost.length).toFixed(0)
      : null,
    co2: energy.length
      ? +(energy.reduce((a, e) => a + e.co2_kg_per_hour, 0) / energy.length).toFixed(1)
      : null,
    ids,
  };
}

/** Where a KPI card's availability figure came from — the demo's `FIG(…, 'shift', …)`. */
export interface DivisionKpiSource {
  /** The week's shift records for the card's machines. */
  readonly filter: RowFilter;
  readonly calc: SourceCalc;
}

/** One KPI card at the head of the division page. */
export interface DivisionKpi {
  /** `'All shown'` or the subclass name. */
  readonly label: string;
  readonly machines: number;
  readonly stat: DivisionStat;
  readonly source: DivisionKpiSource;
}

/**
 * Demo line 2157. The availability figure's drawer: the week's shift records
 * for the card's machines, and the calculation with this card's numbers.
 */
export function divisionKpiSource(stat: DivisionStat, ctx: ShiftCtx): DivisionKpiSource {
  const per = stat.ids
    .map((id) => availOf(id, ctx))
    .filter((v): v is number => v != null);
  const sum = per.reduce((a, b) => a + b, 0);
  const steps = per.length
    ? [
        `Per machine: Σ engine hours ÷ (machine-days × 2 shifts × 9.5 h), ${AVAIL_FROM} to ${AVAIL_TO}`,
        `${per.length} of ${stat.ids.length} machines have shift records · range ${Math.min(...per)}–${Math.max(...per)}%`,
        `Mean = ${sum.toFixed(1)} ÷ ${per.length} = ${stat.avail}%`,
      ]
    : [`No shift records for these machines between ${AVAIL_FROM} and ${AVAIL_TO}.`];
  return {
    filter: { machine_id: stat.ids, date: { between: [AVAIL_FROM, AVAIL_TO] } },
    calc: {
      formula: CALC_NOTE.divisionAvailability,
      steps,
    },
  };
}

/**
 * Demo line 2154. `'All shown'` first, then one card per subclass — but only
 * while no subclass filter is applied, and never more than four cards.
 */
export function divisionKpis(
  machines: readonly Machine[],
  division: DivisionKey,
  subclass: Subclass | null,
  ctx: DivisionCtx,
): DivisionKpi[] {
  const groups: [string, readonly Machine[]][] = [
    ['All shown', machines],
    ...(subclass
      ? []
      : SUBS[division].map(
          (s): [string, readonly Machine[]] => [s, machines.filter((f) => f.subclass === s)],
        )),
  ];
  return groups.slice(0, 4).map(([label, list]) => {
    const stat = divisionStat(list, ctx);
    return { label, machines: list.length, stat, source: divisionKpiSource(stat, ctx) };
  });
}

// ---------------------------------------------------------------------------
// The per-subclass hero
// ---------------------------------------------------------------------------

/** One representative machine, with its place in the class. */
export interface DivisionHero {
  readonly subclass: Subclass;
  readonly machine: Machine;
  /** Every machine of this subclass in the current division, in fleet order. */
  readonly peers: readonly Machine[];
  /** 1-based position of `machine` in `peers`. */
  readonly position: number;
}

/**
 * Demo line 2156. The default pick per subclass: whatever the stepper last
 * selected, else the first machine in an attention band, else the first
 * machine of the class. A subclass with no machines contributes no hero.
 */
export function divisionHeroes(
  machines: readonly Machine[],
  division: DivisionKey,
  subclass: Subclass | null,
  selected: Readonly<Record<string, string | undefined>>,
): DivisionHero[] {
  const wanted = subclass ? [subclass] : SUBS[division];
  return wanted.flatMap((s) => {
    const peers = machines.filter((f) => f.subclass === s);
    if (peers.length === 0) return [];
    const pick =
      peers.find((f) => f.machine_id === selected[s]) ??
      peers.find((f) => ATTENTION_BANDS.includes(statusOf(f.machine_id))) ??
      peers[0];
    return [
      {
        subclass: s,
        machine: pick,
        peers,
        position: peers.findIndex((f) => f.machine_id === pick.machine_id) + 1,
      },
    ];
  });
}

/** Demo line 2158. One `<option>` on the hero's machine picker. */
export function heroOptionLabel(f: Machine, hasWorkings: boolean): string {
  const working = f.working && hasWorkings ? ` · ${f.working.replace(' (Spinifex)', '')}` : '';
  return `${f.machine_id} · ${f.model} · ${MINES[f.mine].name}${working}`;
}

/** Demo line 2159. Step to the previous (`-1`) or next (`+1`) peer, wrapping. */
export function stepPeer(peers: readonly Machine[], machineId: string, delta: number): string {
  if (peers.length === 0) return machineId;
  const i = peers.findIndex((f) => f.machine_id === machineId);
  const next = (i + delta + peers.length) % peers.length;
  return peers[next].machine_id;
}

// ---------------------------------------------------------------------------
// Machines by mine
// ---------------------------------------------------------------------------

/** One mine section at the foot of the division page. */
export interface DivisionMineSection {
  readonly mine: MineCode;
  readonly name: string;
  /** `'12 · live telematics'` — the demo's own subtitle. */
  readonly detail: string;
  readonly cards: readonly MachineCardData[];
}

/** Demo line 2155. Mines in `MINE_ORDER`; a mine with no machines is dropped. */
export function divisionMineSections(
  machines: readonly Machine[],
  ctx: DivisionCtx,
): DivisionMineSection[] {
  return MINE_ORDER.flatMap((mine) => {
    const list = machines.filter((f) => f.mine === mine);
    if (list.length === 0) return [];
    const m = MINES[mine];
    return [
      {
        mine,
        name: m.name,
        detail: `${list.length} · ${m.data_mode === 'Live' ? 'live telematics' : 'shift-end data'}`,
        cards: list.map((f) => machineCardData(f, ctx)),
      },
    ];
  });
}

// ---------------------------------------------------------------------------
// Header copy
// ---------------------------------------------------------------------------

/** Demo line 2153. The page title — `'Surface Drilling › Blasthole drills'`. */
export const divisionTitle = (division: DivisionKey, subclass: Subclass | null): string =>
  subclass ? `${DIVLABEL[division]} › ${subclass}` : DIVLABEL[division];

/**
 * Demo line 2153. The standfirst, verbatim apart from the two figures it
 * interpolates and the demo's "click any figure for the rows behind it", which
 * the port keeps because the KPI cards do open the source drawer.
 */
export function divisionStandfirst(
  division: DivisionKey,
  subclass: Subclass | null,
  machines: readonly Machine[],
): string {
  const lead = subclass ? '' : `Two classes: ${SUBS[division].join(' and ')}. `;
  const mines = new Set(machines.map((f) => f.mine)).size;
  return (
    `${lead}${groupNum(machines.length)} machines across ${mines} mines. Class figures below ` +
    'are means over the machines shown; use the arrows or the drop-down on each render to step ' +
    'through the machines of that class. Click any figure for the rows behind it.'
  );
}

/** The tab row: `All` plus one tab per subclass, with their routes. */
export interface DivisionTab {
  readonly id: string;
  readonly label: string;
  readonly to: string;
  readonly active: boolean;
}

/** Demo line 2153 — the `.tabs` row in the page header. */
export function divisionTabs(division: DivisionKey, subclass: Subclass | null): DivisionTab[] {
  return [
    { id: 'all', label: 'All', to: divisionRoute(division), active: subclass === null },
    ...SUBS[division].map((s) => ({
      id: s,
      label: s,
      to: divisionRoute(division, s),
      active: subclass === s,
    })),
  ];
}

